import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { TAXONOMY } from './constants';
import D2CAllocationListing from './D2CAllocationListing';
import D2CAllocationEdit from './D2CAllocationEdit';
import OtpApprovalModal from './modals/OtpApprovalModal';
import AllocationSourceModal from './modals/AllocationSourceModal';
import PublishModal from './modals/PublishModal';
import HelpDrawer from './modals/HelpDrawer';
import {
  buildAllocationCsvRows, rowsToCsv,
  buildAcMetaFromGroups, buildAcServiceTypesFromGroups,
  buildCostMap, buildRevenueMap,
} from './helpers';
import { useGetServiceabilityGroupsQuery } from '../../../redux/commonApiCalls';
import { useGetSupplyCostsQuery, useGetRevenueGridQuery, useGetAcMetaQuery } from '../../../redux/allocationApiSlice';
import {
  setActiveMerchant,
  clearActiveMerchant,
  closeOtpModal,
  openAllocSourceModal,
  openPublishModal,
} from '../../../redux/d2cAllocationSlice';

export default function D2CAllocation({ mode = 'onboard' }) {
  const dispatch = useDispatch();
  const { merchantId } = useParams();
  const activeMerchantId = useSelector((s) => s.d2cAllocation.activeMerchantId);
  const autoServiceTypes = useSelector((s) => s.d2cAllocation.autoServiceTypes);
  const acEnabled        = useSelector((s) => s.d2cAllocation.acEnabled);
  const groupSameParent  = useSelector((s) => s.d2cAllocation.groupSameParent);
  const forwardMode      = useSelector((s) => s.d2cAllocation.forwardMode);
  const rvpEnabled       = useSelector((s) => s.d2cAllocation.rvpEnabled);
  const customRules      = useSelector((s) => s.d2cAllocation.customRules);
  const reverseRules     = useSelector((s) => s.d2cAllocation.reverseRules);

  const { data: groups = [] }         = useGetServiceabilityGroupsQuery();
  const { data: supplyCostsRaw = [] } = useGetSupplyCostsQuery();
  const { data: revenueGridRaw = [] } = useGetRevenueGridQuery(activeMerchantId, { skip: !activeMerchantId });
  const uploadedFwdRows = useSelector((s) => s.d2cAllocation.merchantFwdRateCardRows || []);
  const uploadedRvpRows = useSelector((s) => s.d2cAllocation.merchantRvpRateCardRows || []);

  const { data: acMetaFromApi = {} } = useGetAcMetaQuery();
  const acMeta         = useMemo(() => Object.keys(acMetaFromApi).length ? acMetaFromApi : buildAcMetaFromGroups(groups), [acMetaFromApi, groups]);
  const acServiceTypes = useMemo(() => buildAcServiceTypesFromGroups(groups), [groups]);
  const costMap        = useMemo(() => buildCostMap(supplyCostsRaw),          [supplyCostsRaw]);
  // Prefer uploaded rate card rows over API revenue grid
  const effectiveRevenueRows = useMemo(
    () => (uploadedFwdRows.length + uploadedRvpRows.length) > 0
      ? [...uploadedFwdRows, ...uploadedRvpRows]
      : revenueGridRaw,
    [uploadedFwdRows, uploadedRvpRows, revenueGridRaw],
  );
  const revenueMap     = useMemo(() => buildRevenueMap(effectiveRevenueRows), [effectiveRevenueRows]);

  useEffect(() => {
    if (merchantId && merchantId !== activeMerchantId) {
      dispatch(setActiveMerchant(merchantId));
      // Direct URL load / page refresh — open source picker so user picks how to proceed.
      dispatch(openAllocSourceModal());
    } else if (!merchantId && activeMerchantId) {
      dispatch(clearActiveMerchant());
      dispatch(closeOtpModal());
    }
  }, [merchantId, activeMerchantId, dispatch]);

  const handleVerified = ({ purpose }) => {
    if (purpose === 'edit_platinum') {
      dispatch(openAllocSourceModal());
    } else if (purpose === 'publish_lossy') {
      dispatch(openPublishModal());
    }
  };

  const handleDownloadCsv = () => {
    const rows = buildAllocationCsvRows({
      autoServiceTypes, acEnabled, groupSameParent, forwardMode, groups,
      acMeta, acServiceTypes, costMap, revenueMap, rvpEnabled, customRules, reverseRules,
    });
    const csv = rowsToCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Proship_Allocation_${activeMerchantId || 'merchant'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Box>
        {activeMerchantId ? <D2CAllocationEdit /> : <D2CAllocationListing />}
      </Box>

      <OtpApprovalModal onVerified={handleVerified} />
      <AllocationSourceModal />
      <PublishModal onDownloadCsv={handleDownloadCsv} />
      <HelpDrawer />
    </>
  );
}
